import { useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import BrandLogo from './BrandLogo';
import '../AllCss/Footer.css';

function Footer() {
  const { t } = useLocale();
  const currentYear = new Date().getFullYear();
  const audioContextRef = useRef(null);
  const lastHoverSoundAtRef = useRef(0);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const playThockHover = useCallback(() => {
    const now = Date.now();
    if (now - lastHoverSoundAtRef.current < 70) return;
    lastHoverSoundAtRef.current = now;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioCtx();
    }

    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    const filterNode = ctx.createBiquadFilter();

    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(230, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(140, ctx.currentTime + 0.055);

    filterNode.type = 'lowpass';
    filterNode.frequency.setValueAtTime(1100, ctx.currentTime);
    filterNode.Q.setValueAtTime(0.7, ctx.currentTime);

    gainNode.gain.setValueAtTime(0.0001, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(1.0, ctx.currentTime + 0.009);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.085);

    oscillator.connect(filterNode);
    filterNode.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.09);
  }, []);

  const socialLinks = [
    {
      key: 'facebook',
      href: process.env.REACT_APP_FACEBOOK_URL,
      label: 'Facebook',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M13.5 21v-7h2.3l.4-3h-2.7V9.1c0-.9.3-1.6 1.7-1.6H16V4.8c-.3 0-1.2-.1-2.3-.1-2.3 0-3.9 1.4-3.9 4V11H7v3h2.8v7h3.7Z" fill="currentColor" />
        </svg>
      ),
    },
    {
      key: 'instagram',
      href: process.env.REACT_APP_INSTAGRAM_URL,
      label: 'Instagram',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M16.5 3h-9A4.5 4.5 0 0 0 3 7.5v9A4.5 4.5 0 0 0 7.5 21h9a4.5 4.5 0 0 0 4.5-4.5v-9A4.5 4.5 0 0 0 16.5 3Zm-4.5 14a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm5.4-9.8a1.2 1.2 0 1 1 0-2.4 1.2 1.2 0 0 1 0 2.4ZM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" fill="currentColor" />
        </svg>
      ),
    },
    {
      key: 'youtube',
      href: process.env.REACT_APP_YOUTUBE_URL,
      label: 'YouTube',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M21.6 8.1a2.9 2.9 0 0 0-2-2C17.8 5.6 12 5.6 12 5.6s-5.8 0-7.6.5a2.9 2.9 0 0 0-2 2A30.7 30.7 0 0 0 2 12a30.7 30.7 0 0 0 .4 3.9 2.9 2.9 0 0 0 2 2c1.8.5 7.6.5 7.6.5s5.8 0 7.6-.5a2.9 2.9 0 0 0 2-2A30.7 30.7 0 0 0 22 12a30.7 30.7 0 0 0-.4-3.9ZM10 15V9l5 3-5 3Z" fill="currentColor" />
        </svg>
      ),
    },
    {
      key: 'tiktok',
      href: process.env.REACT_APP_TIKTOK_URL,
      label: 'TikTok',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M14.5 3v9.2a3.3 3.3 0 1 1-2.8-3.2V6.4a6.7 6.7 0 1 0 5.8 6.6v-4c1 .7 2.3 1.1 3.5 1.2V7a4.6 4.6 0 0 1-3.3-1.4A4.7 4.7 0 0 1 16.8 3h-2.3Z" fill="currentColor" />
        </svg>
      ),
    },
  ];

  const handleSocialClick = (event, href) => {
    if (!href) {
      event.preventDefault();
    }
  };

  return (
    <footer className="footer">
      <div className="footer-main">
        <div className="container">
          <div className="footer-grid">
            {/* Company Info */}
            <div className="footer-col footer-col--brand">
              <Link to="/" className="footer-brand-link" onClick={scrollToTop}>
                <BrandLogo variant="onDark" className="footer-brand-logo" alt="MyCarSlot" />
              </Link>
              <p className="footer-copyright footer-copyright--brand">
                © {currentYear} MyCarSlot. {t('footer.allRightsReserved')}
              </p>
              <div className="footer-app-links">
                <span className="store-badge store-badge-image store-badge--soon" title="App Store — coming soon">
                  <img src="/button-apple-store.webp" alt="" className="store-badge-img" aria-hidden="true" />
                </span>
                <span className="store-badge store-badge-image store-badge--soon" title="Google Play — coming soon">
                  <img src="/button-google-store.webp" alt="" className="store-badge-img" aria-hidden="true" />
                </span>
              </div>
              <div className="footer-social footer-social--brand" aria-label="Social media links">
                {socialLinks.map((social) => (
                  <a
                    key={social.key}
                    href={social.href || '#'}
                    target={social.href ? '_blank' : undefined}
                    rel={social.href ? 'noreferrer' : undefined}
                    className={`social-link ${social.key} ${social.href ? '' : 'social-link--disabled'}`}
                    aria-label={social.label}
                    aria-disabled={!social.href}
                    onClick={(event) => handleSocialClick(event, social.href)}
                    onMouseEnter={playThockHover}
                    onFocus={playThockHover}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Shop */}
            <div className="footer-col">
              <h4>{t('footer.shop')}</h4>
              <ul className="footer-links">
                <li><Link to="/inventory" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.browseInventory')}</Link></li>
                <li><Link to="/finance" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.financingOptions')}</Link></li>
                <li><Link to="/sell/trade" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.sellYourCar')}</Link></li>
                <li><Link to="/sell/trade" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.tradeInValue')}</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div className="footer-col">
              <h4>{t('footer.company')}</h4>
              <ul className="footer-links">
                <li><Link to="/about" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.aboutUs')}</Link></li>
                <li><Link to="/contact" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.contact')}</Link></li>
                <li><Link to="/careers" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.careers')}</Link></li>
                <li><Link to="/blog" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.blog')}</Link></li>
                <li>
                  <Link to="/survey/ideas" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>
                    {t('footer.purchaseExperienceSurvey')}
                  </Link>
                </li>
                <li>
                  <Link to="/partner/register" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>
                    {t('footer.registerPartnerDealer')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div className="footer-col">
              <h4>{t('footer.support')}</h4>
              <ul className="footer-links">
                <li>
                  <Link to="/survey/nps" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>
                    {t('footer.customerFeedbackNps')}
                  </Link>
                </li>
                <li><Link to="/faq-support" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.faq')}</Link></li>
                <li><Link to="/shipping" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.shippingInfo')}</Link></li>
                <li><Link to="/warranty" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.warranty')}</Link></li>
                <li><Link to="/returns" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.returns')}</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div className="footer-col">
              <h4>{t('footer.legal')}</h4>
              <ul className="footer-links">
                <li><Link to="/privacy-policy" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.privacyPolicy')}</Link></li>
                <li><Link to="/terms-of-service" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.termsOfService')}</Link></li>
                <li><Link to="/cookies-policy" onClick={scrollToTop} onMouseEnter={playThockHover} onFocus={playThockHover}>{t('footer.cookiesPolicy')}</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="container">
          <div className="footer-bottom-content">
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;